/**
 * Розбір адреси. Чиста частина роутера — без history, без DOM, без React.
 *
 * Адреса **є ключем горизонту** ([ADR-0013]): навігація в Beresta — це
 * масштаб часу, а не розділи, тож шлях збігається з ментальною моделлю.
 */
import { type Horizon, type PersonId, parseHorizonKey, today } from "@beresta/core";

/** Місця поза часовою віссю. Їх мало навмисно. */
export const PAGES = [
  // Записи — чотири сегменти одного екрана (F1–F4).
  "feed",
  "notes",
  "inbox",
  "reminders",
  "search",
  // Люди
  "people",
  "groups",
  // Ще — корінь M1 і екрани в стеку над ним (WindowMap).
  "more",
  "profile",
  "appearance",
  "sync",
  "lock",
  "encryption",
  "export",
  "about",
  "account",
  "notify",
  "connections",
  "calendars",
  "tasks",
  "contacts",
  "backup",
] as const;
export type PageName = (typeof PAGES)[number];

export type Route =
  | { readonly kind: "horizon"; readonly horizon: Horizon }
  | { readonly kind: "page"; readonly page: PageName }
  /**
   * Картка людини — єдина адреса з двох сегментів.
   *
   * Могла б жити станом усередині списку, і тоді роутер лишився б
   * односегментним. Але картка мусить бути **адресою**: на неї веде згадка
   * `@Людина` з тексту запису ([ADR-0032](../../../../docs/adr/0032-mention-storage.md)),
   * а посилання на стан у чужому компоненті не вказує.
   */
  | { readonly kind: "person"; readonly id: PersonId }
  /**
   * Публічна візитка — [ADR-0037](../../../../docs/adr/0037-public-card.md).
   *
   * Єдина адреса застосунку, яку відкриває **чужа людина**, і єдина, яка
   * працює без акаунта. Двосегментна з тієї ж причини, що й картка
   * людини: на неї ведуть ззовні — з QR-коду, з чужого месенджера, з
   * закладки, — а стан усередині екрана адресою не буває.
   */
  | { readonly kind: "card"; readonly slug: string };

export function isPage(value: string): value is PageName {
  return (PAGES as readonly string[]).includes(value);
}

/** `null` означає «такої адреси немає» — вирішувати, що з цим робити, не тут. */
export function parseRoute(pathname: string): Route | null {
  const segment = pathname.replace(/^\/+|\/+$/g, "");
  if (segment === "") return null;

  // `/people/<id>` — картка. Розбір навмисно нежадібний: людина з
  // порожнім ідентифікатором це не картка, а зіпсована адреса, і вона
  // має впасти в `null`, а не відкрити порожній екран.
  const person = /^people\/([^/]+)$/u.exec(segment);
  if (person?.[1]) return { kind: "person", id: person[1] };

  // `/card/<адреса>` — візитка. Адреса це 22 символи base64url, і саме
  // так вона й перевіряється: усе інше не може бути виданою адресою, а
  // отже не має відкривати екран, який щось вантажить із мережі.
  const card = /^card\/([A-Za-z0-9_-]{22})$/u.exec(segment);
  if (card?.[1]) return { kind: "card", slug: card[1] };

  if (isPage(segment)) return { kind: "page", page: segment };
  const horizon = parseHorizonKey(segment);
  return horizon ? { kind: "horizon", horizon } : null;
}

export function routeToPath(route: Route): string {
  switch (route.kind) {
    case "page":
      return `/${route.page}`;
    case "person":
      return `/people/${route.id}`;
    case "card":
      return `/card/${route.slug}`;
    case "horizon":
      return `/${route.horizon.key}`;
  }
}

/** Ключ маршруту — він же ключ для запамʼятовування прокрутки. */
export function routeKey(route: Route): string {
  return routeToPath(route);
}

/** Куди йти, коли адреса невідома або порожня. День, а не порожній екран. */
export function defaultRoute(timeZone?: string): Route {
  return { kind: "horizon", horizon: { kind: "day", key: today(timeZone).toString() } };
}

export function sameRoute(a: Route | null, b: Route | null): boolean {
  if (!a || !b) return a === b;
  return routeToPath(a) === routeToPath(b);
}
